import { TableStaticActions, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { DeleteContactConfirmationModal } from '@features/contacts/elements/DeleteContactConfirmationModal'
import { SelectedContact } from '@features/contacts/types/SelectedContact'

import { ModalId } from '../../../../../../../../enums/modalId'
import { useOrganizationViewUrl } from '../../../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../utils/routing/reactRoute'
import { ContactsTableActionType } from '../../../../enums/ContactsTableActionType'
import { getContactsTableStaticActions } from '../../utils/getContactsTableStaticActions'

interface ContactsTableStaticActionsProps {
  contactId: string
  contactName: string
}

export const ContactsTableStaticActions = ({
  contactId,
  contactName,
}: ContactsTableStaticActionsProps): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { url: editClientUrl } = useOrganizationViewUrl(reactRoute.organization.clients.getEditClientRoute(contactId))
  const [selectedContact, setSelectedContact] = useState<SelectedContact | null>(null)
  const { open: openDeleteContactConfirmationModal } = useModal(ModalId.DeleteContactConfirmationModal)

  const staticActions = useMemo(() => getContactsTableStaticActions(contactId, t), [contactId, t])

  const handleStaticActionSelect = useCallback(
    (_id: string, value: ContactsTableActionType | 'separator') => {
      if (value === ContactsTableActionType.Edit && editClientUrl) {
        navigate(editClientUrl)
      } else if (value === ContactsTableActionType.Delete) {
        setSelectedContact({ id: contactId, name: contactName })
        openDeleteContactConfirmationModal()
      }
    },
    [contactId, contactName, editClientUrl, navigate, openDeleteContactConfirmationModal],
  )

  const handleDeleteContactConfirmationModalClose = useCallback(() => {
    setSelectedContact(null)
  }, [])

  return (
    <>
      <TableStaticActions items={staticActions} onSelect={handleStaticActionSelect} />
      {selectedContact && (
        <DeleteContactConfirmationModal
          onClose={handleDeleteContactConfirmationModalClose}
          selectedContact={selectedContact}
          variant="contactsList"
        />
      )}
    </>
  )
}
